import styled from '@emotion/styled';
import { PerformanceReportAdAccount, PerformanceReportPeriodPattern } from '@kaizenplatform/hugot-entity';
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import AmazonIcon from '../../primitive/AmazonIcon';
import FacebookIconWithOriginalColor from '../../primitive/FacebookIconWithOriginalColor';
import GoogleIconNew from '../../primitive/GoogleIconNew';

const periods: PerformanceReportPeriodPattern[] = [
  'lifetime',
  'last_7_days',
  'last_30_days',
  'last_week',
  'last_month',
];

type Props = {
  period: PerformanceReportPeriodPattern;
  onPeriodChange: (period: PerformanceReportPeriodPattern) => void;
  adAccount: PerformanceReportAdAccount | null;
  adAccounts: PerformanceReportAdAccount[];
  onAdAccountChange: (accountId: string) => void;
  className?: string;
};

const ReportControl: FC<Props> = ({ period, onPeriodChange, adAccount, adAccounts, onAdAccountChange, className }) => (
  <Root className={className}>
    <PeriodSelector>
      <InputLabel shrink>
        <FormattedMessage id="performance_report_card.options.period.label" />
      </InputLabel>
      <Select
        name="period"
        value={period}
        onChange={(event) => onPeriodChange(event.target.value as PerformanceReportPeriodPattern)}
      >
        {periods.map((periodPattern) => (
          <SizedMenuItem key={'period_' + periodPattern} value={periodPattern}>
            <Typography>
              <FormattedMessage
                id={
                  adAccount?.network === 'amazon' && periodPattern === 'lifetime'
                    ? `performance_report_card.options.period_pattern.${periodPattern.toLowerCase()}_amazon`
                    : `performance_report_card.options.period_pattern.${periodPattern.toLowerCase()}`
                }
              />
            </Typography>
          </SizedMenuItem>
        ))}
      </Select>
    </PeriodSelector>

    <AdAccountSelector>
      <InputLabel shrink>
        <FormattedMessage id="performance_report_headline.ad_account" />
      </InputLabel>
      <Select
        name="ad_account"
        value={adAccount ? adAccount.accountId : ''}
        onChange={(event) => onAdAccountChange(event.target.value as string)}
        displayEmpty
      >
        {adAccounts.map((account) => (
          <SizedMenuItem key={`account_${account.accountId}`} value={account.accountId}>
            <AdAccount>
              {account.network === 'facebook' ? (
                <FacebookIconWithOriginalColor size={16} />
              ) : account.network === 'google' ? (
                <GoogleIconNew size={16} />
              ) : account.network === 'amazon' ? (
                <AmazonIcon size={16} />
              ) : null}

              <AdAccountName>{account.accountId}</AdAccountName>
            </AdAccount>
          </SizedMenuItem>
        ))}
      </Select>
    </AdAccountSelector>
  </Root>
);

const Root = styled('div')({
  ...flexibleLayoutContainer({
    justifyContent: 'flex-end',
  }),
  marginBottom: SPACING_REGULAR,
});

const AdAccountSelector = styled(FormControl)({
  minWidth: 128,
  marginLeft: SPACING_REGULAR,
});

const AdAccount = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const AdAccountName = styled(Typography)({
  marginLeft: SPACING_UNIT,
});

const SizedMenuItem = styled(MenuItem)({
  maxWidth: 360,
});

const PeriodSelector = styled(FormControl)({});

export default ReportControl;
